import { apiService } from '@/services/ApiService';
import { SettingsFieldConfig } from '@/types/model/settingsModal';
import { LookupColumnConfig, LookupRowFieldConfig } from '@/types/model/lookupCrudModal';
import { ContractDetailsResponse } from '@/types/response/InspectionResultResponses';
import {
    SitesResponse, ExpertiseAreasResponse, ClassificationsResponse,
    FrequenciesResponse, FrequencyDetailResponse, FrequencyProfileResponse,
} from '@/types/response/lookupResponses';

// ---- Module Details ----

export interface ModuleDetailsForm {
    name: string;
    customerFeedbackDays: string;
}

export const moduleDetailsEmptyForm: ModuleDetailsForm = { name: '', customerFeedbackDays: '' };

export const moduleDetailsInitialData = (details: ContractDetailsResponse | null): ModuleDetailsForm | null =>
    details ? { name: details.Name ?? '', customerFeedbackDays: details.CustomerFeedbackDays?.toString() ?? '' } : null;

export const moduleDetailsFields: SettingsFieldConfig<ModuleDetailsForm>[] = [
    { key: 'name', label: 'Module Name', type: 'text', validate: v => (!String(v).trim() ? 'Module Name is required.' : undefined) },
    {
        key: 'customerFeedbackDays', label: 'Customer Feedback link expires in x days', type: 'number',
        validate: v => {
            const n = parseInt(String(v), 10);
            if (!String(v).trim()) return 'Customer Feedback Days is required.';
            if (isNaN(n) || n <= 0) return 'Must be a positive integer.';
            return undefined;
        },
    },
];

export const saveModuleDetails = async (form: ModuleDetailsForm, reason?: string): Promise<void> => {
    await apiService.saveModuleDetails({ Name: form.name.trim(), CustomerFeedbackDays: form.customerFeedbackDays.trim(), Reason: reason ?? '' });
};

// ---- Report Settings ----

export interface ReportSettingsForm {
    headerDisplay: boolean;
    footer: string;
}

export const reportSettingsEmptyForm: ReportSettingsForm = { headerDisplay: false, footer: '' };

export const reportSettingsInitialData = (details: ContractDetailsResponse | null): ReportSettingsForm | null =>
    details ? { headerDisplay: details.HeaderDisplay ?? false, footer: details.Footer ?? '' } : null;

export const reportSettingsFields: SettingsFieldConfig<ReportSettingsForm>[] = [
    { key: 'headerDisplay', label: 'Show header on reports', type: 'checkbox' },
    { key: 'footer', label: 'Module Footer', type: 'textarea', placeholder: 'Enter footer text (max 250 characters)...' },
];

export const saveReportSettings = async (form: ReportSettingsForm, reason?: string): Promise<void> => {
    await apiService.saveReportSettings({ HeaderDisplay: form.headerDisplay, Footer: form.footer, Reason: reason ?? '' });
};

// ---- Transaction Logging ----

export interface TransactionLoggingForm {
    logLineItemMod: boolean;
    logInspPlanLock: boolean;
    logInspResMod: boolean;
    logCADel: boolean;
}

export const transactionLoggingEmptyForm: TransactionLoggingForm = {
    logLineItemMod: false, logInspPlanLock: false, logInspResMod: false, logCADel: false,
};

export const transactionLoggingInitialData = (details: ContractDetailsResponse | null): TransactionLoggingForm | null =>
    details ? {
        logLineItemMod: details.LogLineItemMod ?? false,
        logInspPlanLock: details.LogInspPlanLock ?? false,
        logInspResMod: details.LogInspResMod ?? false,
        logCADel: details.LogCADel ?? false,
    } : null;

export const transactionLoggingFields: SettingsFieldConfig<TransactionLoggingForm>[] = [
    { key: 'logLineItemMod', label: 'Log Line Item Add, Edit and Inactivation', type: 'checkbox' },
    { key: 'logInspPlanLock', label: 'Log Inspection Plan Unlock', type: 'checkbox' },
    { key: 'logInspResMod', label: 'Log Inspection Result Edit and Deletion', type: 'checkbox' },
    { key: 'logCADel', label: 'Log Corrective Action Deletion', type: 'checkbox' },
];

export const saveTransactionLogging = async (form: TransactionLoggingForm, reason?: string): Promise<void> => {
    await apiService.saveTransactionLogging({
        LogLineItemMod: form.logLineItemMod,
        LogInspPlanLock: form.logInspPlanLock,
        LogInspResMod: form.logInspResMod,
        LogCADel: form.logCADel,
        Reason: reason ?? '',
    });
};

// ---- Inspection Scheduling ----

export interface InspectionSchedulingForm {
    scheduleIncludeNa: boolean;
    scheduleIncludeDisapproved: boolean;
}

export const inspectionSchedulingEmptyForm: InspectionSchedulingForm = { scheduleIncludeNa: false, scheduleIncludeDisapproved: false };

export const inspectionSchedulingInitialData = (details: ContractDetailsResponse | null): InspectionSchedulingForm | null =>
    details ? {
        scheduleIncludeNa: details.ScheduleIncludeNaInspections ?? false,
        scheduleIncludeDisapproved: details.ScheduleIncludeDisapprovedInspections ?? false,
    } : null;

export const inspectionSchedulingFields: SettingsFieldConfig<InspectionSchedulingForm>[] = [
    { key: 'scheduleIncludeNa', label: 'N/A Inspections still trigger the Next Inspection Date', type: 'checkbox' },
    { key: 'scheduleIncludeDisapproved', label: 'Disapproved Inspections still trigger the Next Inspection Date', type: 'checkbox' },
];

export const saveInspectionScheduling = async (form: InspectionSchedulingForm, reason?: string): Promise<void> => {
    await apiService.saveScheduleImpact({
        ScheduleIncludeNaInspections: form.scheduleIncludeNa,
        ScheduleIncludeDisapprovedInspections: form.scheduleIncludeDisapproved,
        Reason: reason ?? '',
    });
};

// ---- User Type Impact ----

export interface UserTypeImpactForm {
    scheduleAdmins: boolean;
    scheduleProviders: boolean;
    scheduleRecipients: boolean;
    scheduleGuests: boolean;
    freqAdmins: boolean;
    freqProviders: boolean;
    freqRecipients: boolean;
    freqGuests: boolean;
}

export const userTypeImpactEmptyForm: UserTypeImpactForm = {
    scheduleAdmins: false, scheduleProviders: false, scheduleRecipients: false, scheduleGuests: false,
    freqAdmins: false, freqProviders: false, freqRecipients: false, freqGuests: false,
};

export const userTypeImpactInitialData = (details: ContractDetailsResponse | null): UserTypeImpactForm | null =>
    details ? {
        scheduleAdmins: details.ScheduleAdministrators ?? false,
        scheduleProviders: details.ScheduleServiceProviders ?? false,
        scheduleRecipients: details.ScheduleServiceRecipients ?? false,
        scheduleGuests: details.ScheduleGuests ?? false,
        freqAdmins: details.FrequencyAdministrators ?? false,
        freqProviders: details.FrequencyServiceProviders ?? false,
        freqRecipients: details.FrequencyServiceRecipients ?? false,
        freqGuests: details.FrequencyGuests ?? false,
    } : null;

export const userTypeImpactFields: SettingsFieldConfig<UserTypeImpactForm>[] = [
    { key: 'scheduleAdmins', label: 'Administrators', type: 'checkbox', section: 'User Type triggers Next Inspection Date' },
    { key: 'scheduleProviders', label: 'Service Providers', type: 'checkbox', section: 'User Type triggers Next Inspection Date' },
    { key: 'scheduleRecipients', label: 'Service Recipients', type: 'checkbox', section: 'User Type triggers Next Inspection Date' },
    { key: 'scheduleGuests', label: 'Guests', type: 'checkbox', section: 'User Type triggers Next Inspection Date' },
    { key: 'freqAdmins', label: 'Administrators', type: 'checkbox', section: 'User Type contributes to Inspection Frequency Triggers' },
    { key: 'freqProviders', label: 'Service Providers', type: 'checkbox', section: 'User Type contributes to Inspection Frequency Triggers' },
    { key: 'freqRecipients', label: 'Service Recipients', type: 'checkbox', section: 'User Type contributes to Inspection Frequency Triggers' },
    { key: 'freqGuests', label: 'Guests', type: 'checkbox', section: 'User Type contributes to Inspection Frequency Triggers' },
];

export const saveUserTypeImpact = async (form: UserTypeImpactForm, reason?: string): Promise<void> => {
    await apiService.saveFrequencyUserTypes({
        ScheduleAdministrators: form.scheduleAdmins,
        ScheduleServiceProviders: form.scheduleProviders,
        ScheduleServiceRecipients: form.scheduleRecipients,
        ScheduleGuests: form.scheduleGuests,
        FrequencyAdministrators: form.freqAdmins,
        FrequencyServiceProviders: form.freqProviders,
        FrequencyServiceRecipients: form.freqRecipients,
        FrequencyGuests: form.freqGuests,
        Reason: reason ?? '',
    });
};

// ---- Fixed Date Forwarding ----

export interface FixedDateForwardingForm {
    days: string;
}

export const fixedDateForwardingEmptyForm: FixedDateForwardingForm = { days: '' };

export const fixedDateForwardingInitialData = (details: ContractDetailsResponse | null): FixedDateForwardingForm | null =>
    details ? { days: details.NidfPastDueDays?.toString() ?? '' } : null;

export const fixedDateForwardingFields: SettingsFieldConfig<FixedDateForwardingForm>[] = [
    {
        key: 'days', label: 'Advance to next fixed date if inspection outstanding for more than x days', type: 'number',
        validate: v => {
            const n = parseInt(String(v), 10);
            if (!String(v).trim()) return 'This field is required.';
            if (isNaN(n) || n <= 0) return 'Must be a positive integer.';
            return undefined;
        },
    },
];

export const saveFixedDateForwarding = async (form: FixedDateForwardingForm, reason?: string): Promise<void> => {
    await apiService.saveDateForwarding({ NidfPastDueDays: form.days.trim(), Reason: reason ?? '' });
};

// ---- Default Site ----

export interface DefaultSiteForm {
    siteId: string; // 'none' or numeric string
}

export const defaultSiteEmptyForm: DefaultSiteForm = { siteId: 'none' };

export const defaultSiteInitialData = (details: ContractDetailsResponse | null): DefaultSiteForm | null =>
    details ? { siteId: details.DefaultSiteId != null ? details.DefaultSiteId.toString() : 'none' } : null;

export const defaultSiteFields = (sites: SitesResponse[]): SettingsFieldConfig<DefaultSiteForm>[] => [
    {
        key: 'siteId', label: 'Default Site', type: 'select',
        options: [{ value: 'none', label: 'No Default Site' }, ...sites.filter(s => s.IsActive).map(s => ({ value: s.Id.toString(), label: s.Name }))],
        formatView: (value, _form) => {
            if (value === 'none' || !value) return 'No Default Site';
            return sites.find(s => s.Id.toString() === value)?.Name ?? '—';
        },
    },
];

export const saveDefaultSite = async (form: DefaultSiteForm, reason?: string): Promise<void> => {
    await apiService.saveDefaultSite({ SiteId: form.siteId === 'none' ? null : parseInt(form.siteId, 10), Reason: reason ?? '' });
};

// ---- Save In Progress ----

export interface SaveInProgressForm {
    defaultScore: string;
    isNa: boolean;
    autoSaveMinutes: string;
}

export const saveInProgressEmptyForm: SaveInProgressForm = { defaultScore: '', isNa: false, autoSaveMinutes: '' };

export const saveInProgressInitialData = (details: ContractDetailsResponse | null): SaveInProgressForm | null => {
    if (!details) return null;
    const score = details.InstResDefaultScore;
    return {
        defaultScore: score != null ? score.toString() : '',
        isNa: score == null,
        autoSaveMinutes: details.InstResAutoSaveMinutes ?? '',
    };
};

export const saveInProgressFields: SettingsFieldConfig<SaveInProgressForm>[] = [
    { key: 'isNa', label: 'Default Score is N/A', type: 'checkbox' },
    {
        key: 'defaultScore', label: 'Default Score', type: 'number', placeholder: '0–10',
        visible: form => !form.isNa,
        formatView: (value, form) => (form.isNa ? 'N/A' : (value ? String(value) : '—')),
        validate: (value, form) => {
            if (form.isNa) return undefined;
            const score = parseFloat(String(value));
            if (String(value).trim() === '') return 'Default Score is required when N/A is not checked.';
            if (isNaN(score) || score < 0 || score > 10) return 'Score must be between 0 and 10.';
            return undefined;
        },
    },
    {
        key: 'autoSaveMinutes', label: 'Auto Save in Progress (minutes)', type: 'number',
        validate: v => {
            const mins = parseInt(String(v), 10);
            if (!String(v).trim()) return 'Auto Save Minutes is required.';
            if (isNaN(mins) || mins < 0 || mins > 10) return 'Must be between 0 and 10.';
            return undefined;
        },
    },
];

export const saveSaveInProgress = async (form: SaveInProgressForm, reason?: string): Promise<void> => {
    await apiService.saveSaveInProgress({
        InstResDefaultScore: form.isNa ? null : parseFloat(form.defaultScore),
        InstResAutoSaveMinutes: form.autoSaveMinutes.trim(),
        Reason: reason ?? '',
    });
};

// ---- Results Highlighting ----

export interface ResultsHighlightingForm {
    highlightingGood: string;
    highlightingFair: string;
}

export const resultsHighlightingEmptyForm: ResultsHighlightingForm = { highlightingGood: '', highlightingFair: '' };

export const resultsHighlightingInitialData = (details: ContractDetailsResponse | null): ResultsHighlightingForm | null =>
    details ? {
        highlightingGood: details.HighlightingGood ?? '',
        highlightingFair: details.HighlightingFair ?? '',
    } : null;

export const resultsHighlightingFields: SettingsFieldConfig<ResultsHighlightingForm>[] = [
    {
        key: 'highlightingGood', label: 'Good Inspection Result >= x', type: 'number',
        validate: v => {
            const good = parseFloat(String(v));
            if (!String(v).trim()) return 'Required.';
            if (isNaN(good) || good <= 0) return 'Must be a positive number.';
            return undefined;
        },
    },
    {
        key: 'highlightingFair', label: 'Poor Inspection Result <= y', type: 'number',
        validate: v => {
            const fair = parseFloat(String(v));
            if (!String(v).trim()) return 'Required.';
            if (isNaN(fair) || fair <= 0) return 'Must be a positive number.';
            return undefined;
        },
    },
];

export const resultsHighlightingValidateForm = (form: ResultsHighlightingForm): Record<string, string> => {
    const good = parseFloat(form.highlightingGood);
    const fair = parseFloat(form.highlightingFair);
    if (!isNaN(good) && !isNaN(fair) && good <= fair) {
        return { highlightingGood: 'Good threshold must be greater than Poor threshold.' };
    }
    return {};
};

export const saveResultsHighlighting = async (form: ResultsHighlightingForm, reason?: string): Promise<void> => {
    await apiService.saveResultsHighlighting({
        HighlightingGood: form.highlightingGood.trim(),
        HighlightingFair: form.highlightingFair.trim(),
        Reason: reason ?? '',
    });
};

// ---- Areas of Expertise (Lookup CRUD) ----

export interface AreaRow {
    id: number | null;
    name: string;
}

export const areaEmptyRow: AreaRow = { id: null, name: '' };
export const areaToRow = (area: ExpertiseAreasResponse): AreaRow => ({ id: area.Id, name: area.Name });

export const areaColumns: LookupColumnConfig<ExpertiseAreasResponse>[] = [
    { key: 'Name', label: 'Name' },
];

export const areaRowFields: LookupRowFieldConfig<AreaRow>[] = [
    { key: 'name', label: 'Expertise Name', type: 'text', validate: v => (!String(v).trim() ? 'Expertise Name is required.' : undefined) },
];

export const saveAreaRow = async (row: AreaRow, reason: string): Promise<void> => {
    await apiService.saveExpertiseArea({ ...(row.id != null ? { Id: row.id } : {}), Name: row.name.trim(), Reason: reason });
};

// ---- Sites (Lookup CRUD) ----

export interface SiteRow {
    id: number | null;
    name: string;
}

export const siteEmptyRow: SiteRow = { id: null, name: '' };
export const siteToRow = (site: SitesResponse): SiteRow => ({ id: site.Id, name: site.Name });

export const siteColumns: LookupColumnConfig<SitesResponse>[] = [
    { key: 'Name', label: 'Name' },
];

export const siteRowFields = (sites: SitesResponse[]): LookupRowFieldConfig<SiteRow>[] => [
    {
        key: 'name', label: 'Site Name', type: 'text',
        validate: (v, row) => {
            if (!String(v).trim()) return 'Site Name is required.';
            const duplicate = sites.find(s => s.Name.trim().toLowerCase() === String(v).trim().toLowerCase() && s.Id !== row.id);
            if (duplicate) return 'Site name must be unique.';
            return undefined;
        },
    },
];

export const saveSiteRow = async (row: SiteRow, reason: string): Promise<void> => {
    await apiService.saveSite({ ...(row.id != null ? { Id: row.id } : {}), Name: row.name.trim(), Reason: reason });
};

export const activateSiteAction = {
    isActive: (site: SitesResponse) => site.IsActive,
    onToggle: async (site: SitesResponse, _activate: boolean, reason: string) => {
        await apiService.activateSite({ Id: site.Id, Reason: reason });
    },
    reasonTitle: (_site: SitesResponse, activate: boolean) => (activate ? 'Activate Site' : 'Inactivate Site'),
};

// ---- Classifications (Lookup CRUD) ----

export interface ClassificationRow {
    id: number | null;
    name: string;
}

export const classificationEmptyRow: ClassificationRow = { id: null, name: '' };
export const classificationToRow = (c: ClassificationsResponse): ClassificationRow => ({ id: c.Id, name: c.Name });

export const classificationColumns: LookupColumnConfig<ClassificationsResponse>[] = [
    { key: 'Name', label: 'Name' },
];

export const classificationRowFields = (classifications: ClassificationsResponse[]): LookupRowFieldConfig<ClassificationRow>[] => [
    {
        key: 'name', label: 'Classification Name', type: 'text',
        validate: (v, row) => {
            if (!String(v).trim()) return 'Classification Name is required.';
            const duplicate = classifications.find(c => c.Name.trim().toLowerCase() === String(v).trim().toLowerCase() && c.Id !== row.id);
            if (duplicate) return 'Classification name must be unique.';
            return undefined;
        },
    },
];

export const saveClassificationRow = async (row: ClassificationRow, reason: string): Promise<void> => {
    await apiService.saveClassification({ ...(row.id != null ? { Id: row.id } : {}), Name: row.name.trim(), Reason: reason });
};

export const activateClassificationAction = {
    isActive: (c: ClassificationsResponse) => c.IsActive,
    onToggle: async (c: ClassificationsResponse, _activate: boolean, reason: string) => {
        await apiService.activateClassification({ Id: c.Id, Reason: reason });
    },
    reasonTitle: (_c: ClassificationsResponse, activate: boolean) => (activate ? 'Activate Classification' : 'Inactivate Classification'),
};

// ---- Frequency Parameters (Lookup CRUD) ----

export interface FrequencyParameterRow {
    id: number | null;
    name: string;
    days: string;
    history: string;
}

export const frequencyParameterEmptyRow: FrequencyParameterRow = { id: null, name: '', days: '', history: '' };
export const frequencyParameterToRow = (f: FrequencyDetailResponse): FrequencyParameterRow => ({
    id: f.Id, name: f.Name, days: f.Days.toString(), history: f.History.toString(),
});

export const frequencyParameterColumns: LookupColumnConfig<FrequencyDetailResponse>[] = [
    { key: 'Name', label: 'Name' },
    { key: 'Days', label: 'Days', align: 'center' },
    { key: 'History', label: 'History', align: 'center' },
];

export const frequencyParameterRowFields: LookupRowFieldConfig<FrequencyParameterRow>[] = [
    { key: 'name', label: 'Frequency Name', type: 'text', validate: v => (!String(v).trim() ? 'Frequency Name is required.' : undefined) },
    {
        key: 'days', label: 'Length (days)', type: 'number',
        validate: v => {
            const n = parseInt(String(v), 10);
            if (!String(v).trim()) return 'Days is required.';
            if (isNaN(n) || n < 0) return 'Must be a non-negative integer.';
            return undefined;
        },
    },
    {
        key: 'history', label: 'History', type: 'number',
        validate: v => {
            const n = parseInt(String(v), 10);
            if (!String(v).trim()) return 'History is required.';
            if (isNaN(n) || n < 0) return 'Must be a non-negative integer.';
            return undefined;
        },
    },
];

export const saveFrequencyParameterRow = async (row: FrequencyParameterRow, reason: string): Promise<void> => {
    await apiService.saveFrequency({
        ...(row.id != null ? { Id: row.id } : {}),
        Name: row.name.trim(), Days: row.days.trim(), History: row.history.trim(), Reason: reason,
    });
};

// ---- Frequency Profiles (Lookup CRUD) ----

export interface FrequencyProfileRow {
    id: number | null;
    name: string;
    normalFreqId: string;
    normalLow: string;
    normalHi: string;
    decreasedFreqId: string;
    decreasedLow: string;
    increasedFreqId: string;
    increasedHi: string;
}

export const frequencyProfileEmptyRow: FrequencyProfileRow = {
    id: null, name: '', normalFreqId: '', normalLow: '', normalHi: '',
    decreasedFreqId: '', decreasedLow: '', increasedFreqId: '', increasedHi: '',
};

export const frequencyProfileToRow = (p: FrequencyProfileResponse): FrequencyProfileRow => ({
    id: p.Id, name: p.Name,
    normalFreqId: p.NormalFrequencyId.toString(), normalLow: p.NormalLow.toString(), normalHi: p.NormalHi.toString(),
    decreasedFreqId: p.DecreasedFrequencyId.toString(), decreasedLow: p.DecreasedLow.toString(),
    increasedFreqId: p.IncreasedFrequencyId.toString(), increasedHi: p.IncreasedHi.toString(),
});

export const frequencyProfileColumns = (frequencies: FrequenciesResponse[]): LookupColumnConfig<FrequencyProfileResponse>[] => {
    const freqName = (id: number) => frequencies.find(f => f.Id === id)?.Name ?? `#${id}`;
    return [
        { key: 'Name', label: 'Name' },
        { key: 'NormalFrequencyId', label: 'Normal', align: 'center', format: p => `${freqName(p.NormalFrequencyId)} (${p.NormalLow}-${p.NormalHi})` },
        { key: 'DecreasedFrequencyId', label: 'Decreased', align: 'center', format: p => `${freqName(p.DecreasedFrequencyId)} (< ${p.DecreasedLow})` },
        { key: 'IncreasedFrequencyId', label: 'Increased', align: 'center', format: p => `${freqName(p.IncreasedFrequencyId)} (> ${p.IncreasedHi})` },
    ];
};

export const frequencyProfileRowFields = (frequencies: FrequenciesResponse[]): LookupRowFieldConfig<FrequencyProfileRow>[] => {
    const freqOptions = frequencies.map(f => ({ value: f.Id.toString(), label: f.Name }));
    const positiveInt = (label: string) => (v: unknown) => {
        const n = parseInt(String(v), 10);
        if (!String(v).trim()) return `${label} is required.`;
        if (isNaN(n) || n <= 0) return `${label} must be > 0.`;
        return undefined;
    };
    return [
        { key: 'name', label: 'Profile Name', type: 'text', validate: v => (!String(v).trim() ? 'Profile Name is required.' : undefined) },
        { key: 'normalFreqId', label: 'Normal Frequency', type: 'select', options: freqOptions, validate: v => (!String(v) ? 'Required.' : undefined) },
        { key: 'normalLow', label: 'Normal Low', type: 'number', validate: positiveInt('Normal Low') },
        { key: 'normalHi', label: 'Normal High', type: 'number', validate: positiveInt('Normal High') },
        { key: 'decreasedFreqId', label: 'Decreased Frequency', type: 'select', options: freqOptions, validate: v => (!String(v) ? 'Required.' : undefined) },
        { key: 'decreasedLow', label: 'Decreased Low', type: 'number', validate: positiveInt('Decreased Low') },
        { key: 'increasedFreqId', label: 'Increased Frequency', type: 'select', options: freqOptions, validate: v => (!String(v) ? 'Required.' : undefined) },
        { key: 'increasedHi', label: 'Increased High', type: 'number', validate: positiveInt('Increased High') },
    ];
};

export const saveFrequencyProfileRow = async (row: FrequencyProfileRow, reason: string): Promise<void> => {
    await apiService.saveFrequencyProfile({
        ...(row.id != null ? { Id: row.id } : {}),
        Name: row.name.trim(),
        NormalFrequencyId: parseInt(row.normalFreqId, 10), NormalLow: row.normalLow.trim(), NormalHi: row.normalHi.trim(),
        DecreasedFrequencyId: parseInt(row.decreasedFreqId, 10), DecreasedLow: row.decreasedLow.trim(),
        IncreasedFrequencyId: parseInt(row.increasedFreqId, 10), IncreasedHi: row.increasedHi.trim(),
        Reason: reason,
    });
};
